import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ModuleTypeEnum, type Notification } from '@scrambl/shared'

import { queryKeys } from '@/hooks/query-keys'
import { ticketsApi } from '@/lib/api/tickets.api'
import { useEntityRealtime } from '@/lib/realtime'
import { useOrganizationStore } from '@/store/organization.store'

/**
 * Count of unread tickets for the sidebar badge. A ticket is unread when
 * it's relevant to the viewer (assigned, following, inbox notify member,
 * or source alert subscriber) and they haven't opened it since its last
 * activity. Counted server-side; see GET /tickets/unread-count.
 *
 * Realtime: every event that makes a ticket unread also produces a ticket
 * notification for the same users, so a TICKET-entity NOTIFICATION_CREATED
 * is our refetch signal. Mark-read flows update the cache directly.
 */
export function useUnreadTicketsCount(options?: { enabled?: boolean }) {
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const queryClient = useQueryClient()
  const { addEventListener, isConnected } = useEntityRealtime(
    'organization',
    organizationId ?? null,
    { suppressLogging: true },
  )

  const query = useQuery<number>({
    queryKey: queryKeys.tickets.unreadCount(),
    queryFn: async () => {
      if (!organizationId) return 0
      const response = await ticketsApi.getUnreadCount()
      return response.count
    },
    enabled: options?.enabled !== false && !!organizationId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  useEffect(() => {
    if (!organizationId || !isConnected) return

    const handleCreated = (data: { notification?: Notification }) => {
      if (data.notification?.entityType !== ModuleTypeEnum.TICKET) return
      // Refetch instead of mutating the cache — the count depends on
      // server-side relevance + visibility filters we can't replicate here.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.unreadCount(),
      })
    }

    return addEventListener('NOTIFICATION_CREATED', handleCreated, {
      resolveEntityId: data => {
        const typed = data as {
          organizationId?: string
          notification?: { organizationId?: string }
        }
        return (
          typed.organizationId ??
          typed.notification?.organizationId ??
          organizationId ??
          undefined
        )
      },
    })
  }, [addEventListener, isConnected, organizationId, queryClient])

  return query
}
